import { Link } from 'react-router-dom';
import type { SiblingItem } from '../../types/nav';
import { NAV_PILLAR_COLORS } from '../../constants/navPillars';
import { COLORS } from '../../constants/theme';

/**
 * SF-146 Component 4: shared hub-card primitive, extracted from `RealmHub.tsx`'s private
 * `RealmCard` (SF-128) so future Kingdom (SF-150) and World (SF-155) hubs consume a shared
 * component from day one rather than a third copy-paste. Pure markup/props extraction — each
 * hub's `computeStat()` logic stays page-local since stat formulas are domain-specific.
 *
 * SF-167: `stat` widened from a bare string into `HubCardStat` — a primary line, an optional
 * secondary context line, an optional attention dot, and (Buildings only) a ratio bar — so hub
 * cards read as a dashboard rather than a duplicate menu. `HubCard` renders whatever it's given;
 * per-item logic lives in each hub's own `computeStat`.
 *
 * SF-150: reserved `minHeight` on the text column so a card that starts as "Loading…" (label +
 * primary only) doesn't grow and shift a user's tap target once its data resolves — worse for
 * Kingdom than Realm/War since its cards resolve on 3 independently-scheduled hooks instead of
 * one shared `province`. Added `truncate` on the label/primary spans, `data-tone` on the
 * secondary span (a stable test hook, mirroring the existing `data-attention` convention), and
 * `aria-live="polite"` on the text column so a late-arriving stat is announced to screen readers.
 * All four apply to every `HubCard` consumer (Realm/War included), not just Kingdom's.
 */
export interface HubCardStat {
  readonly primary: string;
  readonly secondary?: string;
  readonly secondaryTone?: 'default' | 'warning' | 'critical';
  readonly attention?: 'warning' | 'critical';
  readonly ratio?: { readonly value: number; readonly max: number };
}

interface HubCardProps {
  item: SiblingItem;
  stat: HubCardStat;
}

/** 3% minimum visible width whenever the true ratio is nonzero, so a small-but-real barren
 *  fraction never renders as an imperceptible sliver (Phase 2, ux-designer).
 *  Exported (SF-164) so any capacity/ratio bar elsewhere can reuse the same guard/clamp/floor
 *  instead of redefining it — see `economyStats.ts`'s Economy page capacity bar. */
export function ratioWidthPercent(value: number, max: number): number {
  if (max <= 0) return 0;
  const clamped = Math.max(0, Math.min(value, max));
  if (clamped === 0) return 0;
  return Math.max(3, Math.round((clamped / max) * 100));
}

const SECONDARY_TONE_COLOR: Record<NonNullable<HubCardStat['secondaryTone']>, string> = {
  default: COLORS.text.tertiary,
  warning: COLORS.warning,
  critical: COLORS.negative,
};

export function HubCard({ item, stat }: HubCardProps) {
  const pillar = NAV_PILLAR_COLORS[item.pillar];
  const widthPercent = stat.ratio ? ratioWidthPercent(stat.ratio.value, stat.ratio.max) : 0;

  return (
    <Link
      to={item.route}
      className="flex items-start gap-[11px] rounded-lg p-4 bg-game-800 border border-game-700"
      style={{ borderLeft: `3px solid ${pillar.primary}`, textDecoration: 'none' }}
    >
      <span
        aria-hidden="true"
        className="flex items-center justify-center flex-shrink-0 rounded-md"
        style={{ width: 32, height: 32, backgroundColor: pillar.bgTint }}
      >
        <svg width={18} height={18} viewBox="0 0 16 16" fill="none">
          <path d={item.iconPath} stroke={pillar.primary} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <div className="flex flex-col gap-[2px] min-w-0" style={{ minHeight: 68 }} aria-live="polite">
        <div className="flex items-center gap-[6px]">
          <span className="truncate" style={{ fontSize: 14, fontWeight: 600, color: COLORS.text.primary }}>{item.label}</span>
          {stat.attention && (
            <span
              aria-hidden="true"
              data-attention={stat.attention}
              style={{
                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                backgroundColor: stat.attention === 'critical' ? COLORS.negative : COLORS.warning,
              }}
            />
          )}
        </div>
        <span className="truncate" style={{ fontSize: 13.5, fontWeight: 600, color: COLORS.text.secondary, fontVariantNumeric: 'tabular-nums', marginTop: 1 }}>
          {stat.primary}
        </span>
        {stat.secondary && (
          <span data-tone={stat.secondaryTone} style={{ fontSize: 12, color: SECONDARY_TONE_COLOR[stat.secondaryTone ?? 'default'], marginTop: 2 }}>
            {stat.secondary}
          </span>
        )}
        {stat.ratio && (
          <div
            aria-hidden="true"
            data-ratio-percent={widthPercent}
            style={{ marginTop: 6, height: 4, borderRadius: 2, overflow: 'hidden', background: COLORS.cardAlt }}
          >
            <div style={{ height: '100%', borderRadius: 2, width: `${widthPercent}%`, backgroundColor: pillar.primary }} />
          </div>
        )}
      </div>
    </Link>
  );
}
